import { BaseFeature } from '../../core/base-feature.js';

/**
 * LimitsManifestFeature - Frontend window into the pairing-time hard limits
 *
 * The manifest is co-agreed: the partner can PROPOSE a change and CONFIRM the
 * Subject's proposals, but a change only applies once the OTHER party (the
 * Subject, on the desktop) also signs. The partner can never unilaterally alter
 * the manifest — every request from here is tagged as the partner party by the
 * desktop relay. Status arrives as 'status_update' pushes and limits.* Sacred
 * Timeline events.
 */
export class LimitsManifestFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.pendingRequests = new Map();
        this.currentStatus = null;
    }

    static getMetadata() {
        return {
            id: 'limits-manifest',
            name: 'Hard Limits',
            version: 'v1.0',
            capabilities: ['get_manifest', 'propose', 'confirm'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Limits] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        switch (command) {
            case 'status_update':
                this.currentStatus = payload;
                this.emit('statusUpdate', payload);
                break;

            case 'get_manifest_response':
            case 'get_pending_response':
            case 'propose_response':
            case 'confirm_response':
            case 'cancel_proposal_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this.pendingRequests.has(originalId)) {
                    const { resolve } = this.pendingRequests.get(originalId);
                    this.pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[Limits] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);
            this.pendingRequests.set(messageId, {
                resolve: (result) => { clearTimeout(timeout); resolve(result); },
                reject
            });
        });
    }

    getManifest() { return this._sendRequest('get_manifest'); }
    getPending() { return this._sendRequest('get_pending'); }

    /** Propose a change patch. Applies only after the Subject confirms on desktop. */
    propose(changes) { return this._sendRequest('propose', { changes }); }

    /** Confirm the Subject's pending proposal (the partner's signature). */
    confirm() { return this._sendRequest('confirm'); }

    cancelProposal() { return this._sendRequest('cancel_proposal'); }

    async cleanup() {
        for (const [, { reject }] of this.pendingRequests) reject(new Error('Feature cleanup'));
        this.pendingRequests.clear();
    }
}
